import type { AgentInput, AgentProvider } from "@codvero/shared";
import { PROVIDERS, PROVIDER_SUPPORTS_FAST } from "@codvero/shared";
import { accountsApi, modelsApi } from "@/api/accounts.ts";
import { AgentInput as AgentInputSchema } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { projectsApi } from "@/api/projects.ts";
import { skillsApi, studioApi } from "@/api/studio.ts";
import { SkillCard } from "./SkillsPage.tsx";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Field, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { AgentAvatar, cropToDataUrl } from "@/ui/AgentAvatar.tsx";

const STEPS = ["identity", "instructions", "model", "skills", "permissions", "review"] as const;
type Step = (typeof STEPS)[number];
const EMOJIS = ["🤖", "🧭", "📋", "🏗️", "⚙️", "📊", "🔍", "🧪", "🎨", "🛡️", "✍️", "🧠"];
const COLORS = ["#2f9e7a", "#3b82f6", "#8b5cf6", "#f59e0b", "#06b6d4", "#ef4444", "#ec4899", "#64748b"];

const empty = (): AgentInput => ({ ...AgentInputSchema.parse({ handle: "agent_1", name: "x" }), name: "" });

/** Six-step profile editor, mirroring the original: identity, instructions, model, skills, permissions, review. */
export function AgentWizard({ agentId }: { agentId: string | null }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const existing = useQuery({ queryKey: ["studio", "agents", agentId], queryFn: () => studioApi.agent(agentId!), enabled: !!agentId });
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list });
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list });
  const skills = useQuery({ queryKey: ["studio", "skills"], queryFn: () => skillsApi.list() });
  const [step, setStep] = useState<Step>("identity");
  const [draft, setDraft] = useState<AgentInput | null>(agentId ? null : empty());
  const form = draft ?? (existing.data ? AgentInputSchema.parse(existing.data) : null);
  const set = (patch: Partial<AgentInput>) => setDraft({ ...(form ?? empty()), ...patch });
  const save = useMutation({
    mutationFn: () => (agentId ? studioApi.updateAgent(agentId, form!) : studioApi.createAgent(form!)),
    onSuccess: (a) => { void qc.invalidateQueries({ queryKey: ["studio"] }); void nav({ to: "/agents/$agentId", params: { agentId: a.id }, replace: true }); setDraft(null); setStep("review"); },
  });
  const issues = useMemo(() => (form ? AgentInputSchema.safeParse(form).error?.issues ?? [] : []), [form]);
  if (!form) return <Spinner />;
  const idx = STEPS.indexOf(step);
  const stepDone = (s: Step) => (s === "identity" ? !!form.name && !issues.some((i) => i.path[0] === "handle" || i.path[0] === "name") : s === "instructions" ? !!form.instructions.goal : s === "model" || s === "permissions" || s === "skills" ? true : false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => void nav({ to: "/agents" })}><ArrowLeft className="size-4 rtl:-scale-x-100" /> {t("studio.tabs.agents")}</Button>
          <div>
            <div className="font-semibold">{agentId ? t("studio.wizard.edit") : t("studio.wizard.create")}</div>
            <div className="text-xs text-fg-muted">{form.name || t("studio.wizard.newProfile")}</div>
          </div>
        </div>
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={issues.length > 0}>{t("common.save")}</Button>
      </div>
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
      {/* phone: a compact progress header instead of six labelled buttons */}
      <div className="space-y-2 rounded-lg border border-border bg-surface p-3 md:hidden">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t(`studio.steps.${step}`)}</span>
          <span className="text-xs text-fg-muted">{t("studio.stepOf", { n: STEPS.indexOf(step) + 1, total: STEPS.length })}</span>
        </div>
        <div className="flex gap-1.5">
          {STEPS.map((s) => (
            <button key={s} type="button" aria-label={t(`studio.steps.${s}`)} onClick={() => setStep(s)} className="tap flex flex-1 items-center">
              <span className={cn("h-1.5 w-full rounded-full", step === s ? "bg-accent" : stepDone(s) ? "bg-success/60" : "bg-border")} />
            </button>
          ))}
        </div>
      </div>
      <nav className="hidden gap-1 overflow-x-auto rounded-lg border border-border bg-surface p-1 md:flex">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(s)} className={cn("flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm", step === s ? "bg-accent-soft font-medium" : "text-fg-muted hover:text-fg")}>
            <span className={cn("grid size-5 place-items-center rounded-full border text-[10px]", stepDone(s) ? "border-success bg-success-soft text-success" : "border-border")}>{stepDone(s) ? <Check className="size-3" /> : i + 1}</span>
            {t(`studio.steps.${s}`)}
          </button>
        ))}
      </nav>

      <div className="rounded-lg border border-border bg-surface p-5">
        <h2 className="text-lg font-semibold">{t(`studio.steps.${step}`)}</h2>
        <p className="mb-4 text-sm text-fg-muted">{t(`studio.stepsDesc.${step}`)}</p>

        {step === "identity" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("studio.fields.name")} error={issues.find((i) => i.path[0] === "name")?.message}><Input value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
            <Field label={t("studio.fields.handle")} hint={t("studio.fields.handleHint")} error={issues.find((i) => i.path[0] === "handle")?.message}><Input dir="ltr" className="font-mono" value={form.handle} onChange={(e) => set({ handle: e.target.value.toLowerCase() })} /></Field>
            <Field label={t("studio.fields.title")}><Input value={form.title} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label={t("studio.fields.language")}>
              <Select value={form.language} onChange={(e) => set({ language: e.target.value as "ar" | "en" })}><option value="ar">العربية</option><option value="en">English</option></Select>
            </Field>
            <div className="sm:col-span-2"><Field label={t("studio.fields.description")}><Input value={form.description} onChange={(e) => set({ description: e.target.value })} /></Field></div>
            <div>
              <div className="mb-1.5 text-sm font-medium">{t("studio.fields.avatar")}</div>
              <div className="mb-2 flex items-center gap-3">
                <AgentAvatar avatar={form.avatar} size="lg" />
                <label className="cursor-pointer rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-2">
                  {t("studio.fields.photo")}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) set({ avatar: { ...form.avatar, image: await cropToDataUrl(f) } }); }} />
                </label>
                {form.avatar.image && <button type="button" className="text-xs text-fg-muted hover:text-danger" onClick={() => set({ avatar: { ...form.avatar, image: null } })}>{t("studio.fields.photoRemove")}</button>}
              </div>
              <div className="flex flex-wrap gap-1">{EMOJIS.map((e) => <button key={e} type="button" className={cn("size-9 rounded-md border text-lg", form.avatar.emoji === e ? "border-accent bg-accent-soft" : "border-border")} onClick={() => set({ avatar: { ...form.avatar, emoji: e } })}>{e}</button>)}</div>
              <div className="mt-2 flex flex-wrap gap-1">{COLORS.map((c) => <button key={c} type="button" aria-label={c} className={cn("size-7 rounded-full border-2", form.avatar.color === c ? "border-fg" : "border-transparent")} style={{ background: c }} onClick={() => set({ avatar: { ...form.avatar, color: c } })} />)}</div>
            </div>
            <div>
              <div className="mb-1.5 text-sm font-medium">{t("studio.fields.projects")}</div>
              <p className="mb-1 text-xs text-fg-muted">{form.projectIds.length === 0 ? t("studio.fields.projectsAll") : t("studio.fields.projectsSome", { count: form.projectIds.length })}</p>
              <div className="max-h-40 space-y-1 overflow-auto rounded-md border border-border p-2 text-sm">
                {(projects.data ?? []).map((p) => (
                  <label key={p.id} className="flex items-center gap-2"><input type="checkbox" checked={form.projectIds.includes(p.id)} onChange={(e) => set({ projectIds: e.target.checked ? [...form.projectIds, p.id] : form.projectIds.filter((x) => x !== p.id) })} /> {p.name}</label>
                ))}
                {projects.data?.length === 0 && <span className="text-xs text-fg-muted">{t("projects.empty")}</span>}
              </div>
            </div>
            <Field label={t("studio.fields.status")}>
              <Select value={form.status} onChange={(e) => set({ status: e.target.value as AgentInput["status"] })}>
                {(["DRAFT", "ACTIVE", "DISABLED"] as const).map((s) => <option key={s} value={s}>{t(`studio.status.${s}`)}</option>)}
              </Select>
            </Field>
          </div>
        )}

        {step === "instructions" && (
          <div className="grid gap-4">
            {(["goal", "responsibilities", "constraints", "workStyle", "outputs", "systemPrompt", "forbidden"] as const).map((k) => (
              <Field key={k} label={t(`studio.instructions.${k}`)} hint={t(`studio.instructionsHint.${k}`)}>
                <textarea dir="auto" className="min-h-20 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:border-accent focus:outline-none" value={form.instructions[k]} onChange={(e) => set({ instructions: { ...form.instructions, [k]: e.target.value } })} />
              </Field>
            ))}
          </div>
        )}

        {step === "model" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <ModelFields form={form} set={set} accounts={accounts.data ?? []} />
            <Field label={t("studio.model.thinking")}>
              <Select value={form.model.thinking} onChange={(e) => set({ model: { ...form.model, thinking: e.target.value as AgentInput["model"]["thinking"] } })}>{(["auto", "low", "medium", "high", "max"] as const).map((m) => <option key={m} value={m}>{m}</option>)}</Select>
            </Field>
            <Field label={t("studio.model.timeout")}><Input dir="ltr" type="number" min={60} max={7200} value={form.model.timeoutSec} onChange={(e) => set({ model: { ...form.model, timeoutSec: Number(e.target.value) } })} /></Field>
            <Field label={t("studio.model.attempts")}><Input dir="ltr" type="number" min={1} max={5} value={form.model.maxAttempts} onChange={(e) => set({ model: { ...form.model, maxAttempts: Number(e.target.value) } })} /></Field>
            <Field label={t("studio.model.turns")}><Input dir="ltr" type="number" min={1} max={500} value={form.model.maxTurns} onChange={(e) => set({ model: { ...form.model, maxTurns: Number(e.target.value) } })} /></Field>
            <p className="text-xs text-fg-muted sm:col-span-2">{t("studio.model.note")}</p>
          </div>
        )}

        {step === "skills" && (
          <div className="space-y-3">
            {/* reviewed skills, plus any already assigned to this agent (even if not reviewed right now): a skill
                missing from the list would be dropped from the agent on save */}
            {(skills.data ?? []).filter((sk) => sk.status === "reviewed" || form.skillIds.includes(sk.id)).length === 0 && <p className="text-sm text-fg-muted">{t("studio.skills.noneReviewed")}</p>}
            <div className="grid gap-3 md:grid-cols-2">
              {(skills.data ?? []).filter((sk) => sk.status === "reviewed" || form.skillIds.includes(sk.id)).map((sk) => (
                <label key={sk.id} className={cn("flex cursor-pointer items-start gap-3 rounded-lg border p-2", form.skillIds.includes(sk.id) ? "border-accent bg-accent-soft/30" : "border-border")}>
                  <input type="checkbox" className="mt-3" checked={form.skillIds.includes(sk.id)} onChange={(e) => set({ skillIds: e.target.checked ? [...form.skillIds, sk.id] : form.skillIds.filter((x) => x !== sk.id) })} />
                  <div className="min-w-0 flex-1"><SkillCard s={sk} compact /></div>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === "permissions" && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t("studio.permissions.capabilities")}</h3>
              {(["read", "edit", "run", "network", "browser", "secrets", "writeOutside", "integrations"] as const).map((k) => (
                <label key={k} className={cn("flex items-start gap-3 rounded-md border p-3", ["run", "network", "secrets", "writeOutside"].includes(k) && form.permissions[k] ? "border-danger/40" : "border-border")}>
                  <input type="checkbox" className="mt-0.5" checked={form.permissions[k]} onChange={(e) => set({ permissions: { ...form.permissions, [k]: e.target.checked } })} />
                  <span><span className="text-sm">{t(`studio.permissions.${k}`)}</span><span className="block text-xs text-fg-muted">{t(`studio.permissionsHint.${k}`)}</span></span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t("studio.permissions.askFirst")}</h3>
              {(["edits", "terminal", "internet", "secrets", "handoff"] as const).map((k) => (
                <label key={k} className="flex items-center gap-3 rounded-md border border-border p-3">
                  <input type="checkbox" checked={form.permissions.approvals[k]} onChange={(e) => set({ permissions: { ...form.permissions, approvals: { ...form.permissions.approvals, [k]: e.target.checked } } })} />
                  <span className="text-sm">{t(`studio.approvals.${k}`)}</span>
                </label>
              ))}
              <div className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
                <div className="font-medium text-fg">{t("studio.permissions.summaryTitle")}</div>
                <p>{summary(form, t)}</p>
                <p className="mt-1">{t("studio.permissions.boundary")}</p>
              </div>
            </div>
          </div>
        )}

        {step === "review" && (
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Row k={t("studio.review.agent")} v={`${form.avatar.emoji} ${form.name || "—"} · @${form.handle}`} />
            <Row k={t("studio.review.role")} v={form.title || "—"} />
            <Row k={t("studio.review.config")} v={`${PROVIDERS[form.model.provider].label} · ${form.model.model === "default" ? t("chat.models.default") : form.model.model} · ${form.model.thinking}${form.model.speed === "fast" ? ` · ${t("chat.speed.fast")}` : ""}`} />
            <Row k={t("studio.review.scope")} v={form.projectIds.length ? t("studio.fields.projectsSome", { count: form.projectIds.length }) : t("studio.fields.projectsAll")} />
            <div className="sm:col-span-2"><Row k={t("studio.review.permissions")} v={summary(form, t)} /></div>
            <div className="sm:col-span-2"><Row k={t("studio.instructions.goal")} v={form.instructions.goal || "—"} /></div>
            {issues.length > 0 && <div className="sm:col-span-2"><Alert tone="warn">{issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(" · ")}</Alert></div>}
          </dl>
        )}

        <div className="mt-5 flex justify-between">
          <Button variant="ghost" disabled={idx === 0} onClick={() => setStep(STEPS[idx - 1]!)}>{t("common.back")}</Button>
          {idx < STEPS.length - 1 ? <Button variant="secondary" onClick={() => setStep(STEPS[idx + 1]!)}>{t("common.continue")}</Button> : <Button onClick={() => save.mutate()} loading={save.isPending} disabled={issues.length > 0}>{t("common.save")}</Button>}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="rounded-md border border-border p-3"><dt className="text-[10px] uppercase tracking-wide text-fg-muted">{k}</dt><dd className="mt-0.5 whitespace-pre-wrap">{v}</dd></div>;
}

function summary(f: AgentInput, t: TFunction): string {
  const caps = (["read", "edit", "run", "network", "browser", "secrets", "writeOutside", "integrations"] as const).filter((k) => f.permissions[k]).map((k) => t(`studio.permissions.${k}`));
  const gates = (["edits", "terminal", "internet", "secrets", "handoff"] as const).filter((k) => f.permissions.approvals[k]).length;
  return t("studio.permissions.summary", { caps: caps.join("، ") || "—", gates });
}

/** Provider (from the connected accounts), model (live catalog for that provider) and speed, like the chat settings sheet. */
function ModelFields({ form, set, accounts }: { form: AgentInput; set: (patch: Partial<AgentInput>) => void; accounts: { id: string; provider: AgentProvider; label: string; status: string }[] }) {
  const { t } = useTranslation();
  const provider = form.model.provider;
  const connected = accounts.filter((a) => a.status === "connected");
  const account = connected.find((a) => a.provider === provider) ?? null;
  const providers = (Object.keys(PROVIDERS) as AgentProvider[]).filter((p) => connected.some((a) => a.provider === p) || p === provider);
  const catalog = useQuery({ queryKey: ["models", provider, account?.id ?? null], queryFn: () => modelsApi.catalog(provider, account?.id ?? null), staleTime: 5 * 60_000 });
  const models = catalog.data?.models ?? [];
  const known = models.some((m) => m.id === form.model.model);
  return (
    <>
      <Field label={t("studio.model.provider")}>
        <Select value={provider} onChange={(e) => set({ model: { ...form.model, provider: e.target.value as AgentProvider, model: "default", speed: "standard" } })}>
          {providers.map((p) => <option key={p} value={p}>{PROVIDERS[p].vendor} · {PROVIDERS[p].label}{connected.some((a) => a.provider === p) ? "" : ` (${t("accounts.status.pending")})`}</option>)}
        </Select>
        <p className="mt-1 text-[11px] text-fg-muted">{account ? t("studio.model.accountHint", { label: account.label }) : t("studio.model.noAccount")}</p>
      </Field>
      <Field label={t("studio.model.model")}>
        <Select value={form.model.model} onChange={(e) => set({ model: { ...form.model, model: e.target.value } })}>
          {models.map((m) => <option key={m.id} value={m.id}>{m.id === "default" ? t("chat.models.default") : m.label}{m.isDefault && m.id !== "default" ? ` · ${t("chat.modelAccountDefault")}` : ""}</option>)}
          {!known && <option value={form.model.model}>{form.model.model}</option>}
        </Select>
        <p className="mt-1 text-[11px] text-fg-muted">{catalog.isLoading ? t("chat.modelsLoading") : catalog.data ? (catalog.data.exact ? t("chat.modelsLive") : t("chat.modelsCatalog", { source: catalog.data.source })) : ""}</p>
      </Field>
      <Field label={t("chat.speed.label")}>
        <Select value={form.model.speed} disabled={!PROVIDER_SUPPORTS_FAST[provider]} onChange={(e) => set({ model: { ...form.model, speed: e.target.value as AgentInput["model"]["speed"] } })}>
          <option value="standard">{t("chat.speed.standard")}</option>
          <option value="fast">{t("chat.speed.fast")}</option>
        </Select>
        {!PROVIDER_SUPPORTS_FAST[provider] && <p className="mt-1 text-[11px] text-fg-muted">{t("studio.model.noSpeed")}</p>}
      </Field>
    </>
  );
}
